import { DsmPageContainer } from '@/components';
import PageHeader from '@/components/PageHeader';
import { formatActivityDate } from '@/dsm-components';
import usePermissions from '@/hooks/usePermissions';
import { isErrorWithData } from '@/services/helpers';
import {
  useDeactivateProductLineMutation,
  useGetProductLineQuery,
  useGetProductLineUnmatchedQuery,
  useUpdateProductLineValuesMutation,
} from '@/services/productLines';
import { PalmettoButton, PalmettoModal, PalmettoTag } from '@palmetto/dsm-components';
import { toast } from '@palmetto/palmetto-components';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { EquipmentRecordStatus, ErrorCode, ProgramType, UserPermissions } from 'types';

import {
  emptyValueLabel,
  EquipmentRecordStatusLabels,
  EquipmentRecordStatusTagColors,
  formatEquipmentHistoryValue,
} from '../equipment-manufacturers/equipmentLabels';
import { EntityHistoryFeed } from '../shared/EntityHistoryFeed';

import { ProductLineBreadcrumb } from './ProductLineBreadcrumb';
import { atLeastOneValueMessage, cleanProductLineValues, ProductLineValuesEditor } from './ProductLineValuesEditor';
import { ProductLineValueTags } from './ProductLineValueTags';

const fieldLabels: Record<string, string> = { values: 'Values', status: 'Status' };
const productLineFieldLabel = (field: string): string => fieldLabels[field] ?? field;

const MetaField = ({ label, value }: { label: string; value?: string }) => (
  <div className="flex flex-col gap-4">
    <div className="body-300 text-text-secondary">{label}</div>
    <div className="body-bold-300 text-text-primary">{value && value.trim() !== '' ? value : emptyValueLabel}</div>
  </div>
);

export const ProductLineDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { data: item, isLoading } = useGetProductLineQuery(id ?? skipToken);
  const { data: unmatched } = useGetProductLineUnmatchedQuery(ProgramType.energyBackup);
  const canEditAvl = usePermissions().includes(UserPermissions.canImportAvlData);

  const [updateValues, { isLoading: isSaving }] = useUpdateProductLineValuesMutation();
  const [deactivateProductLine, { isLoading: isDeactivating }] = useDeactivateProductLineMutation();

  const [draftValues, setDraftValues] = useState<string[] | null>(null);
  const [valuesError, setValuesError] = useState<string | undefined>();
  const [isDeactivateOpen, setIsDeactivateOpen] = useState(false);

  const isActive = item?.status === EquipmentRecordStatus.active;
  const canEdit = canEditAvl && isActive;

  const orphanValues = useMemo(
    () =>
      new Set(
        (unmatched?.orphanValues ?? [])
          .filter((orphan) => orphan.productLineId === id)
          .map((orphan) => orphan.value),
      ),
    [unmatched, id],
  );

  const handleEditValues = useCallback(() => {
    setValuesError(undefined);
    setDraftValues(item?.values.length ? [...item.values] : ['']);
  }, [item]);

  const handleCancelValues = useCallback(() => {
    setValuesError(undefined);
    setDraftValues(null);
  }, []);

  const handleSaveValues = useCallback(async () => {
    if (!item || !draftValues) return;
    const values = cleanProductLineValues(draftValues);
    if (values.length === 0) {
      setValuesError(atLeastOneValueMessage);
      return;
    }

    try {
      await updateValues({ id: item.id, values }).unwrap();
      toast.success('Product line values updated');
      setDraftValues(null);
      setValuesError(undefined);
    } catch (error) {
      console.error('Error updating product line values:', error);
      if (isErrorWithData(error) && error.data.errorCode === ErrorCode.equipmentDuplicateAlias) {
        setValuesError(error.data.message);
      } else {
        toast.error(isErrorWithData(error) ? error.data.message : 'Failed to update product line values.');
      }
    }
  }, [item, draftValues, updateValues]);

  const handleOpenDeactivate = useCallback(() => setIsDeactivateOpen(true), []);
  const handleCloseDeactivate = useCallback(() => setIsDeactivateOpen(false), []);

  const handleConfirmDeactivate = useCallback(async () => {
    if (!item) return;
    try {
      await deactivateProductLine(item.id).unwrap();
      toast.success(`${item.name} deactivated.`);
      setIsDeactivateOpen(false);
      setDraftValues(null);
    } catch (error) {
      console.error('Error deactivating product line:', error);
      toast.error(isErrorWithData(error) ? error.data.message : 'Failed to deactivate product line.');
    }
  }, [item, deactivateProductLine]);

  if (isLoading) {
    return (
      <DsmPageContainer title="Loading...">
        <div>Loading...</div>
      </DsmPageContainer>
    );
  }

  if (!item) {
    return (
      <DsmPageContainer title="Not Found">
        <div>Product line not found.</div>
      </DsmPageContainer>
    );
  }

  return (
    <DsmPageContainer title={item.name}>
      <PageHeader title={item.name} eyebrow={<ProductLineBreadcrumb current={item.name} />}>
        <div className="flex items-center gap-8">
          <PalmettoTag
            label={EquipmentRecordStatusLabels[item.status] ?? item.status}
            color={EquipmentRecordStatusTagColors[item.status]}
          />
          {canEdit && (
            <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={handleOpenDeactivate}>
              Deactivate
            </PalmettoButton>
          )}
        </div>
      </PageHeader>

      <div className="mx-6 mb-16 grid grid-cols-3 gap-16">
        <div className="flex flex-col gap-4">
          <div className="body-300 text-text-secondary">Name</div>
          <div className="body-bold-300 text-text-primary">{item.name}</div>
          <div className="body-100 text-text-tertiary">
            Names can't be changed. To rename a line, deactivate it and create a new one.
          </div>
        </div>
        <MetaField label="Classification" value={item.classificationPath} />
        <MetaField label="Modified By" value={item.modifiedByName ?? item.createdByName} />
        <MetaField
          label="Created"
          value={item.meta?.createdAt ? formatActivityDate(item.meta.createdAt) : undefined}
        />
        <MetaField
          label="Last Modified"
          value={item.meta?.modifiedAt ? formatActivityDate(item.meta.modifiedAt) : undefined}
        />
      </div>

      <div className="mx-6 mb-16 flex flex-col gap-8">
        {draftValues !== null ? (
          <>
            <ProductLineValuesEditor
              value={draftValues}
              onChange={setDraftValues}
              disabled={isSaving}
              errorMessage={valuesError}
            />
            <div className="flex justify-end gap-8">
              <PalmettoButton
                type="button"
                emphasis="secondary"
                layout="hug"
                onClick={handleCancelValues}
                disabled={isSaving}
              >
                Cancel
              </PalmettoButton>
              <PalmettoButton
                type="button"
                emphasis="primary"
                layout="hug"
                onClick={handleSaveValues}
                disabled={isSaving}
              >
                Save Values
              </PalmettoButton>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <h2 className="text-left heading-200 text-text-primary">Product Catalog values</h2>
              {canEdit && (
                <PalmettoButton type="button" emphasis="secondary" layout="hug" onClick={handleEditValues}>
                  Edit Values
                </PalmettoButton>
              )}
            </div>
            <ProductLineValueTags values={item.values} orphanValues={orphanValues} />
          </>
        )}
      </div>

      <div className="mx-6 mb-16 flex flex-col gap-4">
        <h2 className="text-left heading-200 text-text-primary">Change History</h2>
        <EntityHistoryFeed
          history={item.history ?? []}
          fieldLabel={productLineFieldLabel}
          formatValue={formatEquipmentHistoryValue}
        />
      </div>

      {isDeactivateOpen && (
        <PalmettoModal
          open
          onClose={handleCloseDeactivate}
          invertedBackground={false}
          bottomContent={
            <div className="flex justify-end gap-8">
              <PalmettoButton
                type="button"
                emphasis="secondary"
                layout="hug"
                onClick={handleCloseDeactivate}
                disabled={isDeactivating}
              >
                Cancel
              </PalmettoButton>
              <PalmettoButton
                type="button"
                emphasis="primary"
                layout="hug"
                onClick={handleConfirmDeactivate}
                disabled={isDeactivating}
              >
                Deactivate
              </PalmettoButton>
            </div>
          }
        >
          <div className="flex flex-col gap-16">
            <h2>Deactivate {item.name}?</h2>
            <p className="text-text-secondary">
              Deactivation is permanent; a deactivated product line can't be reactivated. Its batteries will resolve
              to no product line until their values are added to another line, and its name and values become free to
              reuse.
            </p>
          </div>
        </PalmettoModal>
      )}
    </DsmPageContainer>
  );
};
